/*
 * Copyright 2023 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import axios, { AxiosError } from "axios";
import React, { FunctionComponent, useEffect } from "react";
import {
    getFirstLastNameFromString,
    LOCAL_STORAGE_SOCIAL_USER_METADATA_KEY,
    UTM_KEYS,
} from "../../utils/signup-form/signup-form.util";
import {
    FormDataPayLoad,
    StoredFormDataForHubspot,
} from "./submit-user-to-hubspot-provider.interfaces";
import {
    HS_CONTACT_ID,
    SENTRY_EVENT_MSG,
    SUBMISSION_URL,
} from "./submit-user-to-hubspot-provider.utils";

/**
 * Social sign up for Auth0 does not allow sending metadata. We store this in
 * localStorage and send the data along with user email and name to hubspot right
 * after successful authentication
 */
export const SubmitUserToHubspotProvider: FunctionComponent = ({
    children,
}) => {
    // Note to use auth0's user and not useAuthProvider's
    const { user } = useAuth0();

    // This is expected to run after Auth0 completest its request to check if user is authenticated
    useEffect(() => {
        if (user && user.email) {
            const storedMetadata = window.localStorage.getItem(
                LOCAL_STORAGE_SOCIAL_USER_METADATA_KEY
            );

            if (!storedMetadata) {
                return;
            }

            /**
             * Auth0 action "determine-if-client-should-send-to-hubspot"
             * determines the `clientShouldSendToHubspotSend` flag
             *
             * Account for undefined should the script fail to injected something
             */
            const cancelOverrideFromAuth0 =
                user.clientShouldSendToHubspotSend === false;

            if (cancelOverrideFromAuth0) {
                // Delete entry
                window.localStorage.removeItem(
                    LOCAL_STORAGE_SOCIAL_USER_METADATA_KEY
                );

                return;
            }

            try {
                const savedMetadata: StoredFormDataForHubspot =
                    JSON.parse(storedMetadata);

                let firstName = user.given_name;
                let lastName = user.family_name;

                // Typically happens when github sign up is used
                if ((!firstName || !lastName) && user.name) {
                    [firstName, lastName] = getFirstLastNameFromString(
                        user.name
                    );

                    if (!lastName) {
                        lastName = "not provided";
                    }
                }

                const formProperties = [
                    {
                        objectTypeId: HS_CONTACT_ID, // objectTypeId: "0-1" associates field with `contact` object
                        name: "firstname",
                        value: firstName,
                    },
                    {
                        objectTypeId: HS_CONTACT_ID,
                        name: "lastname",
                        value: lastName,
                    },
                    {
                        objectTypeId: HS_CONTACT_ID,
                        name: "email",
                        value: user.email,
                    },
                    {
                        objectTypeId: HS_CONTACT_ID,
                        name: "forms_filled",
                        value: "Try StarTree Cloud",
                    },
                    {
                        objectTypeId: HS_CONTACT_ID,
                        name: "i_agree_to_the_startree_community_terms_and_conditions_",
                        value: "Yes",
                    },
                ];

                // If utm parameters are in user_metadata
                UTM_KEYS.forEach((utmKey) => {
                    if (savedMetadata.hsFormPropertyValues[utmKey]) {
                        formProperties.push({
                            objectTypeId: HS_CONTACT_ID,
                            name: utmKey,
                            value: savedMetadata.hsFormPropertyValues[utmKey],
                        });
                    }
                });

                const formData: FormDataPayLoad = {
                    fields: formProperties,
                    context: {
                        pageUri: `${window.location.origin}/signup`,
                    },
                };

                // Add hubspot tracking code if it exists in the metadata
                if (savedMetadata.hsFormMetadata?.hutk) {
                    formData.context.hutk = savedMetadata.hsFormMetadata.hutk;
                }

                axios
                    .post(SUBMISSION_URL, formData, {
                        headers: {
                            referer: savedMetadata.hsFormMetadata?.referer,
                        },
                    })
                    .then(
                        () => {
                            // Delete entry when completed
                            window.localStorage.removeItem(
                                LOCAL_STORAGE_SOCIAL_USER_METADATA_KEY
                            );
                        },
                        (axiosError: AxiosError) => {
                            Sentry.captureEvent({
                                message: SENTRY_EVENT_MSG,
                                tags: {
                                    hsErrorResponse:
                                        axiosError.response?.status,
                                },
                                extra: {
                                    hsErrorMsg:
                                        axiosError.response?.data?.message,
                                },
                            });
                        }
                    );
            } catch (e) {
                Sentry.captureEvent({
                    message: SENTRY_EVENT_MSG,
                    extra: {
                        stack: (e as Error)?.stack,
                    },
                });
            }
        }
    }, []);

    return <>{children}</>;
};
